import { ExchangeMarketType } from '@bitly/sdk/types'
import { useRouter } from 'next/router'
import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import ChangePercent from 'components/ChangePercent'
import ColoredPrice from 'components/ColoredPrice'
import Currency from 'components/Currency'
import { FlexDivRowCentered } from 'components/layout/flex'
import MarketBadge from 'components/MarketBadge'
import Spacer from 'components/Spacer'
import Table, { TableHeader } from 'components/Table'
import ROUTES from 'constants/routes'
import { selectMarketVolumes } from 'state/exchange/selectors'
import { useAppSelector } from 'state/hooks'
import { selectPreviousDayPrices, selectPrices, selectOraclePrices } from 'state/prices/selectors'
import { selectMarkets } from 'state/exchange/selectors'
import { formatDollars, formatNumber } from 'utils/prices'
import { PriceChange } from 'state/prices/types'

type FuturesMarketsTableProps = {
	search?: string
}

const sortBy = [{ id: 'dailyVolume', desc: true }]

const FuturesMarketsTable: React.FC<FuturesMarketsTableProps> = ({ search }) => {
	const { t } = useTranslation()
	const router = useRouter()

	const futuresMarkets = useAppSelector(selectMarkets)
	const laggedPrices = useAppSelector(selectPreviousDayPrices)
	const marketsVolumes = useAppSelector(selectMarketVolumes)
	const marketPrices = useAppSelector(selectPrices)
	const oraclePrices = useAppSelector(selectOraclePrices)

	// Store quote prices in USD
	const [quotePrices, setQuotePrices] = useState<Record<string, number>>({})
	
	// Get oracle prices for quote symbols from Redux state
	useEffect(() => {
		if (futuresMarkets.length > 0 && oraclePrices) {
			const uniqueQuoteSymbols = [...new Set(futuresMarkets.map(market => market.tokenY.symbol))]
			const prices: Record<string, number> = {}
			
			for (const symbol of uniqueQuoteSymbols) {
				const price = oraclePrices[symbol]
				if (price) {
					prices[symbol] = price
				}
			}
			
			setQuotePrices(prices)
		}
	}, [futuresMarkets, oraclePrices])
	
	let data = useMemo(() => {
		const lowerSearch = search?.toLowerCase()
		const markets: ExchangeMarketType[] = lowerSearch
			? (futuresMarkets as ExchangeMarketType[]).filter(
					(m) =>
						m.marketAddress.toLowerCase().includes(lowerSearch) ||
						m.displayName?.toLowerCase().includes(lowerSearch)
			  )
			: futuresMarkets
		return markets.map((market) => {
			const tokenName = market.tokenX.name
			const icon = market.tokenX.logo
            const quoteIcon = market.tokenY.logo
            const quoteSymbol = market.tokenY.symbol
			const marketAddress = market.marketAddress
			const description = market.displayName
			const volume = marketsVolumes[marketAddress]
			const pastPrice = laggedPrices[marketAddress] ?? 0
			const marketPrice = marketPrices[marketAddress] ?? 0
			const change: PriceChange = (()=>{
				if (marketPrice - pastPrice > 0) {
					return 'up'
				} else if (marketPrice - pastPrice < 0) {
					return 'down'
				} else {
					return null
				}
			})()

			return {
				asset: market.marketAddress,
				tokenName,
				icon,
                quoteIcon,
                quoteSymbol,
				description,
				price: marketPrice,
                quotePrice: quotePrices[quoteSymbol],
				change,
				volume,
				pastPrice: pastPrice,
				priceChange:
					pastPrice && marketPrice > 0
						? (marketPrice - pastPrice) / marketPrice 
						: 0,
			}
		})
	}, [search, futuresMarkets, t, marketsVolumes, laggedPrices, marketPrices, quotePrices])

	return (
        <TableContainer>
            <StyledTable
                data={data}
                onTableRowClick={(row) => {
                    router.push(ROUTES.Markets.Home(row.original.asset))
                }}
                highlightRowsOnHover
                sortBy={sortBy}
                columns={[
                    {
                        header: () => (
                            <TableHeader>{t('dashboard.overview.futures-markets-table.market')}</TableHeader>
                        ),
                        accessorKey: 'market',
                        cell: (cellProps) => {
                            return (
                                <MarketContainer>
                                    <IconContainer>
                                        <OverlappingIconsContainer>
                                            <PrimaryIconWrapper>
                                                <StyledCurrencyIcon
                                                    currencyKey={cellProps.row.original.asset}
                                                    url={cellProps.row.original.icon}
                                                />
                                            </PrimaryIconWrapper>
                                            <SecondaryIconWrapper>
                                                <StyledCurrencyIcon
                                                    currencyKey={cellProps.row.original.asset}
                                                    url={cellProps.row.original.quoteIcon}
                                                />
                                            </SecondaryIconWrapper>
                                        </OverlappingIconsContainer>
                                    </IconContainer>
                                    <StyledText>
                                        {cellProps.row.original.description}
                                        <Spacer width={8} />
                                        <MarketBadge
                                            currencyKey={cellProps.row.original.asset}
                                            isFuturesMarketClosed={false}
                                            futuresClosureReason={undefined}
                                        />
                                    </StyledText>
                                    <StyledValue>{cellProps.row.original.tokenName}</StyledValue>
                                </MarketContainer>
                            )
                        },
                        size: 190,
                        enableSorting: true,
                    },
                    {
                        id: 'dailyVolume',
                        header: () => (
                            <TableHeader>
                                {t('dashboard.overview.futures-markets-table.daily-volume')}
                            </TableHeader>
                        ),
                        accessorKey: 'dailyVolume',
                        cell: (cellProps) => {
                            return (
                                <Currency.Price
                                    price={cellProps.row.original.volume}
                                    formatOptions={{ truncateOver: 1e3 }}
                                />
                            )
                        },
                        size: 125,
                        enableSorting: true,
                        sortingFn: (a, b) => a.original.volume > b.original.volume ? 1 : -1,
                    },
                    {
                        header: () => (
                            <TableHeader>
                                {t('dashboard.overview.futures-markets-table.mark-price')}
                            </TableHeader>
                        ),
                        accessorKey: 'price',
                        cell: (cellProps) => {
                            const quoteSymbol = cellProps.row.original.quoteSymbol
                            const quotePrice = cellProps.row.original.quotePrice
                            const usdPrice = quotePrice * cellProps.row.original.price
                            
                            return (
                                <div>
                                    <ColoredPrice priceChange={cellProps.row.original.change}>
                                        {formatNumber(cellProps.row.original.price, {
                                            suggestDecimalsForAsset: cellProps.row.original.asset,
                                        })}
                                        {' $'}
                                        {quoteSymbol}
                                    </ColoredPrice>
                                    {quotePrice > 0 && (
                                        <USDPrice>
                                            {formatDollars(usdPrice, {
                                                suggestDecimals: true,
                                            })}
                                        </USDPrice>
                                    )}
                                </div>
                            )
                        },
                        size: 130,
                        enableSorting: true,
                        sortingFn: (a, b) => a.original.price > b.original.price ? 1 : -1,
                    },
                    {
                        header: () => (
                            <TableHeader>
                                {t('dashboard.overview.futures-markets-table.daily-change')}
                            </TableHeader>
                        ),
                        accessorKey: 'priceChange',
                        cell: (cellProps) => {
                            return (
                                <ChangePercent
                                    value={cellProps.row.original.priceChange}
                                    decimals={2}
                                    className="change-pct"
                                />
                            )
                        },
                        size: 105,
                        enableSorting: true,
                        sortingFn: (a, b) => a.original.priceChange > b.original.priceChange ? 1 : -1,
                    },
                ]}
            />
        </TableContainer>
	)
}

const StyledCurrencyIcon = styled(Currency.Icon)`
	width: 30px;
	height: 30px;
`

const OpenInterestContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
`

const IconContainer = styled.div`
	grid-column: 1;
	grid-row: 1 / span 2;
	position: relative;
	width: 45px;
	height: 30px;
`

const OverlappingIconsContainer = styled.div`
	position: relative;
	width: 45px;
	height: 30px;
`

const PrimaryIconWrapper = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	z-index: 2;
`

const SecondaryIconWrapper = styled.div`
	position: absolute;
	top: 0;
	left: 15px; /* 50% overlap with the primary icon (30px width / 2) */
	z-index: 1;
`

const StyledValue = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	font-family: ${(props) => props.theme.fonts.regular};
	font-size: 12px;
	grid-column: 2;
	grid-row: 2;
`

const TableContainer = styled.div`
	margin: 16px 0 40px;

	.paused {
		color: ${(props) => props.theme.colors.selectedTheme.gray};
	}
`

const StyledTable = styled(Table)`
	margin-bottom: 20px;
` as typeof Table

const StyledText = styled(FlexDivRowCentered)`
	justify-content: space-between;
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	font-family: ${(props) => props.theme.fonts.bold};
`

const MarketContainer = styled.div`
	display: grid;
	grid-template-rows: auto auto;
	grid-template-columns: 60px auto;
	align-items: center;
	width: 200px;
	column-gap: 10px;
`

const StyledMobileTable = styled(StyledTable)`
	margin-top: 4px;
	border-radius: initial;
	border-top: none;
	border-left: none;
	border-right: none;
` as typeof Table

const USDPrice = styled.div`
	font-size: 12px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	margin-top: 4px;
`

export default FuturesMarketsTable
